import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { WatchlistEmptyIcon } from '../item/icons';
import { WatchlistList } from './WatchlistList';
import { WatchlistHeader } from './WatchlistHeader';
import { Watchlist } from '../../types/Watchlist';

interface WatchlistPanelProps {
  watchlist?: Watchlist | null;
  error?: unknown;
}

// D7 — one shared centred layout, driving four+ copy sets. The copy must never collapse: a
// pending/failed/never-generated row rendered with the empty state's "no concerns" words is the
// false negative a screening tool exists to avoid (contract §8).
const Message = ({ titleKey, bodyKey, reason }: { titleKey: string; bodyKey: string; reason?: string }) => {
  const { t } = useTranslation('common');

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 10,
        px: 3,
        textAlign: 'center',
        color: 'primary.main',
      }}
    >
      <WatchlistEmptyIcon fontSize={56} />
      <Typography variant="h3" sx={{ mt: 3, color: 'text.primary' }}>
        {t(titleKey)}
      </Typography>
      <Typography variant="subtitle1" sx={{ mt: 1, maxWidth: 560, color: 'text.secondary' }}>
        {t(bodyKey)}
      </Typography>
      {reason && (
        <Typography variant="subtitle1" sx={{ mt: 1, maxWidth: 560, color: 'text.secondary' }}>
          {t('application.watchlist.state.failed.reason', { reason })}
        </Typography>
      )}
    </Box>
  );
};

// Five render states off one frozen query (PLAN-FRONTEND.md §5 F5) — `error`, `null` (never
// generated), `pending`, `completed` w/ items, `completed` w/ zero items, `failed`, plus an
// unrecognised status (a future contract value this build predates) falling back to the failed
// copy — AC-12b: never the empty copy.
export const WatchlistPanel = ({ watchlist, error }: WatchlistPanelProps) => {
  if (error) {
    return (
      <Message titleKey="application.watchlist.state.error.title" bodyKey="application.watchlist.state.error.body" />
    );
  }

  if (watchlist == null) {
    return (
      <Message titleKey="application.watchlist.state.none.title" bodyKey="application.watchlist.state.none.body" />
    );
  }

  if (watchlist.status === 'pending') {
    return (
      <Message
        titleKey="application.watchlist.state.pending.title"
        bodyKey="application.watchlist.state.pending.body"
      />
    );
  }

  if (watchlist.status === 'completed' && watchlist.items.length > 0) {
    return <WatchlistList watchlist={watchlist} />;
  }

  if (watchlist.status === 'completed') {
    return (
      <>
        <WatchlistHeader generatedAt={watchlist.generatedAt} />
        <Message titleKey="application.watchlist.state.empty.title" bodyKey="application.watchlist.state.empty.body" />
      </>
    );
  }

  return (
    <>
      <WatchlistHeader generatedAt={watchlist.generatedAt} />
      <Message
        titleKey="application.watchlist.state.failed.title"
        bodyKey="application.watchlist.state.failed.body"
        reason={watchlist.status === 'failed' ? watchlist.failureReason : undefined}
      />
    </>
  );
};
